import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '../../../../../../../ui/toast';
import {
  useCreateOrUpdateCCAConfigMutation,
  useLazyListCCAQuestionsQuery,
  useListCCAQuestionsQuery,
  useUpdateCCAConfigQuestionsMutation,
} from '../../../../../../../utils/api_service/ccaApi';
import {
  CCAConfig,
  CCAConfigQuestion,
  CCAQuestion,
} from '../../../../../../../utils/types/cca_types';
import { CCASubdomain } from '../../../../../../questions/CCABank/ccaConstants';

interface UseManualSelectionProps {
  jobOpeningId: string;
  existingConfig?: CCAConfig;
}

export const useManualSelection = ({
  jobOpeningId,
  existingConfig,
}: UseManualSelectionProps) => {
  const [searchText, setSearchText] = useState('');
  const [filterSubdomain, setFilterSubdomain] = useState<CCASubdomain | 'All'>('All');
  const [filterLevel, setFilterLevel] = useState<number | 'All'>('All');
  const [filterType, setFilterType] = useState<string>('All');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<CCAQuestion[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const preloadedRef = useRef(false);

  const [fetchAll] = useLazyListCCAQuestionsQuery();
  const [saveQuestions] = useUpdateCCAConfigQuestionsMutation();
  const [saveConfig] = useCreateOrUpdateCCAConfigMutation();

  // Pre-populate selected from assigned questions on first load
  useEffect(() => {
    if (preloadedRef.current) return;
    const assigned = existingConfig?.questions;
    if (!assigned?.length) return;
    preloadedRef.current = true;
    setIsPreloading(true);
    fetchAll({ page: 1, page_size: 20 })
      .unwrap()
      .then((result) => {
        const idSet = new Set(assigned.map((q) => q.questionId));
        const fullQs = result.items.filter((q) => idSet.has(q.id));
        const orderMap = new Map(
          assigned.map((q) => [q.questionId, q.orderKey]),
        );
        fullQs.sort(
          (a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0),
        );
        setSelected(fullQs);
      })
      .catch(() => {})
      .finally(() => setIsPreloading(false));
  }, [existingConfig?.questions]); // eslint-disable-line

  const resetPage = () => setPage(1);

  const { data, isLoading: isListLoading } = useListCCAQuestionsQuery({
    subdomain:
      filterSubdomain !== 'All'
        ? filterSubdomain.toLowerCase().replace(/ /g, '_')
        : undefined,
    level: filterLevel !== 'All' ? filterLevel : undefined,
    question_type: filterType !== 'All' ? filterType : undefined,
    page,
    page_size: 25,
  });

  const selectedIds = useMemo(
    () => new Set(selected.map((q) => q.id)),
    [selected],
  );

  const visibleQuestions = useMemo(() => {
    if (!data?.items) return [];
    const lower = searchText.toLowerCase();
    return data.items.filter((q) => {
      if (selectedIds.has(q.id)) return false;
      if (searchText && !q.question.text.toLowerCase().includes(lower))
        return false;
      return true;
    });
  }, [data, searchText, selectedIds]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addQuestion = (q: CCAQuestion) => setSelected((p) => [...p, q]);
  const removeQuestion = (id: string) =>
    setSelected((p) => p.filter((q) => q.id !== id));
  const clearSelected = () => setSelected([]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Ensure config exists with manual mode (no quickConfig)
      await saveConfig({
        jobOpeningId,
        body: { enabled: true },
      }).unwrap();
      const questionsPayload: CCAConfigQuestion[] = selected.map((q, idx) => ({
        questionId: q.id,
        orderKey: idx + 1,
        timeLimitSeconds: 60,
      }));
      await saveQuestions({
        jobOpeningId,
        body: { questions: questionsPayload },
      }).unwrap();
      toast.success('Selection saved.', { position: 'bottom-center' });
    } catch {
      toast.error('Failed to save selection.', { position: 'bottom-center' });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    searchText,
    setSearchText,
    filterSubdomain,
    setFilterSubdomain,
    filterLevel,
    setFilterLevel,
    filterType,
    setFilterType,
    page,
    setPage,
    selected,
    expandedIds,
    isSaving,
    isPreloading,
    isListLoading,
    visibleQuestions,
    total,
    totalPages,
    resetPage,
    toggleExpand,
    addQuestion,
    removeQuestion,
    clearSelected,
    handleSave,
  };
};
